import {useLayoutEffect, useState, type CSSProperties, type RefObject} from 'react';
import {useTranslation} from 'react-i18next';
import {useCountBelowViewport} from '@chekinapp/ui';
import {FloatingPill} from 'components/FloatingPill';
import {prefersReducedMotion} from 'utils/prefersReducedMotion';

const BOTTOM_SLACK_PX = 24;
const SCROLL_STEP = 0.8;

type Props = {
  rosterRef: RefObject<HTMLDivElement | null>;
};

function MoreGuestsPill({rosterRef}: Props) {
  const {t} = useTranslation();
  const [columnStyle, setColumnStyle] = useState<CSSProperties>();
  const below = useCountBelowViewport(rosterRef, {
    selector: '[data-roster-row]',
    bottomOffset: BOTTOM_SLACK_PX,
  });

  const isVisible = below > 0;

  useLayoutEffect(() => {
    const roster = rosterRef.current;
    if (!isVisible || !roster) return;

    const measure = () => {
      const {left, width} = roster.getBoundingClientRect();
      setColumnStyle({left, width, right: 'auto'});
    };
    measure();

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(roster);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isVisible, rosterRef]);

  const handleClick = () => {
    window.scrollBy({
      top: window.innerHeight * SCROLL_STEP,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  return (
    <FloatingPill
      visible={isVisible}
      containerStyle={columnStyle}
      aria-label={t('scroll_to_more_guests')}
      onClick={handleClick}
    >
      {t('n_more_guests', {count: below})}
    </FloatingPill>
  );
}

export {MoreGuestsPill};
